<template>
  <o-loading :full-page="true" :active="isLoading" :can-cancel="false"></o-loading>
  <div v-if="$_.isEmpty(customFields?.fields) && dataLoaded">
    <h4 class="is-italic is-size-3 is-italic has-text-weight-semibold mb-4">
        Company Custom Fields
      </h4>
    <h3 class="is-size-3 has-text-black-bis text-center has-text-weight-bold is-flex is-align-items-center is-justify-content-center py-5">
        No Custom Fields configured yet, Please Contact your Administrator to set up custom fields.
      </h3>
  </div>
  <h4 v-else class="is-italic is-size-3 is-italic has-text-weight-semibold mb-4">
    Company Custom Fields
  </h4>
  <div class="columns is-multiline has-seperator mb-5 pb-5">
    <div class="column is-3" v-for="cf in customFields?.fields" :key="cf._id">
      <div v-if="!editMode">
        <label class="label is-size-3 has-text-black-bis is-italic"
        v-tooltip="cf.label.toLowerCase().toString().length > 24 ? cf.label : ''">
            {{ cf.label}}
        </label>
        <div v-if="cf.__t === 'Checkbox'" class="is-pulled-left">
          <o-checkbox
            v-model="cf.value"
            disabled
          >
          </o-checkbox>
        </div>
        <h3 v-else class="is-size-3 has-text-black-ter has-text-weight-normal"
          v-tooltip="cfToolTip(cf)">
          {{ cfValue(cf) }}
        </h3>
      </div>
      <div v-if="editMode">
        <label class="label is-size-3 has-text-black-bis is-italic"
        v-tooltip="cf.label.toLowerCase().toString().length > 24 ? cf.label : ''">
        {{ cf.label}}
        </label>
        <div v-if="cf.__t === 'Dropdown'">
          <mf-multi-select
            v-model="cf.value"
            :options="cf.options"
            track-by="_id"
            label="displayValue"
            :multiple="false"
            :allow-empty="false"
            :closeOnSelect="true"
            :placeholder="cf.placeholder"
            @update:modelValue="() => updateCfValue(cf)">

          </mf-multi-select>
        </div>
        <div v-if="cf.__t === 'Number'">
          <qty-input type="number" min="0" :max="QTY_Max"
            v-model.number="cf.value" :roundTo="4"
            :value="cf.value"
            @update:value="(val) => { cf.value = val; updateCfValue(cf); }"
            @blur="validateFields(cf)" :class="cf.hasError ? 'archived-border' : ''"
          ></qty-input>
        </div>
        <div v-if="cf.__t === 'String'">
          <input class="input" type="text" v-model.trim="cf.value" @input="updateCfValue(cf)" @blur="validateFields(cf)" :placeholder="cf?.placeholder"
          :class="cf.hasError ? 'archived-border' : ''"/>
        </div>
        <div v-if="cf.__t === 'Date'" class="cf-modal-body is-pulled-left">
          <mf-date
            :item="cf"
            :is-edit="true"
            :input-props="{pos: 'bottom-right'}"
            :placeholder='"mm/dd/yyyy"'
            :isCustomFields="true"
            @set-date="($event) => { cf.value = $event.val; updateCfValue(cf); }"
          />
        </div>
          <div v-if="cf.__t === 'Checkbox'" class="is-pulled-left">
          <o-checkbox
            v-model="cf.value"
            @update:modelValue="() => updateCfValue(cf)"
          >
          </o-checkbox>
        </div> 
      </div>      
    </div>
  </div>
</template>

<script>

import {
  reactive, toRefs, onBeforeMount,
} from 'vue';
import {
  find, filter, findIndex,
} from 'lodash';
import { useStore } from 'vuex';
import moment from 'moment';
import CustomFields from '@/models/CustomFields';
import MfDate from '@/components/abstract/MfDate.vue';
import QtyInput from '@/components/fields/QtyInput.vue';
import Validations from '@/utils/Validations';
import QtyOptions from '../utils/QtyOptions';
import { screenLabelMap } from '@/components/mixins/UtilityMixin';

export default {
  name: 'CustomFieldsOrderView',
  components: {
    MfDate,
    QtyInput,
  },
  props: {
    card: {
      type: Object,
      default: () => ({}),
    },
    editMode: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const store = useStore();
    const state = reactive({
      QTY_Max: QtyOptions.CF_QTY_MAX,
      customFields: {},
      user: {},
      dataLoaded: false,
      isLoading: false,
    });

    state.user = store.state.userData;

    onBeforeMount( async () => {
      state.isLoading = true;
      const cfType = props.card.__t === 'ProductionOrder' ? 'po_order' : props.card?.parentShipment ? 'sl_delivery' : 'sl_order';
      state.customFields = await CustomFields.getCustomFields({ type: cfType });
      if (_.isEmpty(state.customFields?.fields)) {
        state.dataLoaded = true;
        state.isLoading = false;
        return;
      }
      state.customFields.fields.forEach((cf) => { 
        const existingCf = find(props.card.customFields, ['_id', cf._id]);
        cf.value = existingCf 
          ? (cf.__t === 'Dropdown' ? find(cf.options, (opts) => opts?._id === existingCf?.value?._id) : existingCf.value)
          : '';
        if(existingCf && cf.uniqueAcross) existingCf.uniqueAcross = cf.uniqueAcross;
      });
      if (state.customFields.fields.length != props.card.customFields.length) {
        const newCfs = filter(state.customFields.fields, (field) => !find(props.card?.customFields, ['_id', field._id]));
        newCfs.forEach((cf) => {
          const cfToAdd = {
            _id: cf._id,
            value: '',  
          };
          if(cf.uniqueAcross) cfToAdd.uniqueAcross = cf.uniqueAcross;
          props.card.customFields.push(cfToAdd);
        });
      };
      props.card.customFields = props.card.customFields.map((cf) => {
        cf = { ...cf, __t: find(state.customFields.fields, ['_id', cf._id])?.__t };
        return cf;
      })
      state.dataLoaded = true;
      state.isLoading = false;
    });

    const cfValue = (cf) => {
      if (cf.__t === 'Dropdown') {
        return cf.value?.displayValue || 'No option selected';
      }
      if (cf.__t === 'Date') {
        return cf.value ? moment(cf.value).format('MM/DD/YYYY') : 'MM/DD/YYYY';
      }
      if (cf.__t === 'String') {
        return cf.value ? cf.value : 'No text set';
      }
      if (cf.__t === 'Number') {
        return cf.value ? cf.value : 'No number set';
      }
      return cf.value || 'No value set';
    };

    const updateCfValue = (cf) => {
      const updatedCard = {...props.card};
      if (!updatedCard.customFields) {
        updatedCard.customFields = [];
      }
      const existingCfIndex = findIndex(updatedCard.customFields, ['_id', cf._id]);
      if (existingCfIndex > -1) {
        if (cf.__t === 'Date') {
          updatedCard.customFields[existingCfIndex].value = moment(cf.value).toISOString();
        } else {
          cf.__t === 'Dropdown' ? updatedCard.customFields[existingCfIndex].value = cf.value
          : updatedCard.customFields[existingCfIndex].value = cf.value;
        }
      } else {
        updatedCard.customFields.push({
          _id: cf._id,
          value: cf.__t === 'Date' ? moment(cf.value).toISOString() : cf.value,
        });
      };
      if (state.customFields.fields.length != updatedCard.customFields.length) {
        const newCfs = filter(state.customFields.fields, (field) => !find(updatedCard.customFields, ['_id', field._id]));
        newCfs.forEach((cf) => {
          updatedCard.customFields.push({
            _id: cf._id,
            value: '',
          });
        });
      };
      props.card.customFields = updatedCard.customFields;
    };
    const tabType = () => {
      const screen = store.state.activeScreen === 'shipping-card-edit' ? 'shipping-custom-fields' : 'prod-custom-fields';
      return screenLabelMap[screen]?.tab || '';
    };
    const validateFields = async (cf) => {
      const isValid = await Validations.validateCustomFields(cf, props.card, state.user, false, tabType());
      return isValid;
    }

    const cfToolTip = (cf) => {
      const value = cfValue(cf);
      return cf.__t === 'Number' ? value : (value.toLowerCase().length > 24 ? value : '');
    };

    return {
      ...toRefs(state),
      updateCfValue,
      validateFields,
      cfValue,
      cfToolTip,
    };
  },
}

</script>